<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Product Management</h1>
            <p class="text-orange-100 text-lg">Manage all products in the catalogue</p>
          </div>
          <div class="flex space-x-3">
            <router-link
              to="/admin/labels"
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200"
            >
              <svg class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Manage Labels
            </router-link>
            <button
              @click="openAddProductModal"
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200"
            >
              <svg class="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Add Product
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Filters -->
    <div class="mb-8">
      <GlobalFilters @filter-change="handleFilterChange" />
    </div>

    <!-- Search Results Counter -->
    <div v-if="hasActiveFilters" class="mb-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <svg class="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span class="text-blue-800 font-medium">
              Showing {{ filteredProducts.length }} of {{ pagination?.total || 0 }} products
            </span>
          </div>
          <button 
            @click="filtersStore.clearAllFilters"
            class="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Clear all filters
          </button>
        </div>
      </div>
    </div>

    <!-- Products Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      <div v-for="product in filteredProducts" :key="product.id" class="group bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-200 flex flex-col h-full">
        <!-- Product Image -->
        <div class="aspect-w-1 aspect-h-1 bg-black">
          <div 
            @click="openImageModal(product)"
            class="w-full h-48 bg-black flex items-center justify-center relative overflow-hidden cursor-pointer group"
          >
            <img
              v-if="getProductImages(product).length > 0"
              :src="getImageUrl(getProductImages(product)[0])"
              :alt="product.name"
              class="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
              @error="handleImageError"
            />
            <div v-else class="placeholder w-full h-full bg-black flex items-center justify-center">
              <span class="text-white text-lg font-bold">{{ product.name.charAt(0) }}</span>
            </div>
            <!-- View Images Overlay -->
            <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <div class="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium text-gray-900">
                <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View Images
              </div>
            </div>
          </div>
        </div>
        
        <!-- Product Info -->
        <div class="p-6 flex flex-col flex-grow">
          <div class="flex justify-between items-start mb-3">
            <h3 class="text-lg font-semibold text-gray-900">{{ product.name }}</h3>
            <span class="text-sm text-gray-500">{{ product.sku }}</span>
          </div>
          
          <!-- Minimal Info (Always Visible) -->
          <div class="space-y-2 mb-3">
            <p class="text-sm text-gray-600">
              <span class="font-medium">Category:</span> {{ product.category }}
            </p>
            <p class="text-sm text-gray-600">
              <span class="font-medium">Metal:</span> {{ product.metal }}
            </p>
            <p v-if="product.vendor_name" class="text-sm text-gray-600">
              <span class="font-medium">Vendor:</span> {{ product.vendor_name }}
            </p>
          </div>

          <!-- Expandable Details -->
          <div v-if="expandedProducts.includes(product.id)" class="space-y-2 mb-4 border-t pt-3">
            <p v-if="product.gross_weight" class="text-sm text-gray-600">
              <span class="font-medium">Gross Weight:</span> {{ product.gross_weight }}g
            </p>
            <p v-if="product.stone_weight" class="text-sm text-gray-600">
              <span class="font-medium">Stone Weight:</span> {{ product.stone_weight }}g
            </p>
            <p v-if="product.net_weight" class="text-sm text-gray-600">
              <span class="font-medium">Net Weight:</span> {{ product.net_weight }}g
            </p>
            <p v-if="product.purchase_rate" class="text-sm text-gray-600">
              <span class="font-medium">Purchase Rate:</span> ₹{{ product.purchase_rate }}
            </p>
            <p v-if="product.selling_rate" class="text-sm text-gray-600">
              <span class="font-medium">Selling Rate:</span> ₹{{ product.selling_rate }}
            </p>
            <p v-if="product.making_charge" class="text-sm text-gray-600">
              <span class="font-medium">Making Charge:</span> ₹{{ product.making_charge }}
            </p>
            <p v-if="product.stone_charge" class="text-sm text-gray-600">
              <span class="font-medium">Stone Charge:</span> ₹{{ product.stone_charge }}
            </p>
            <p v-if="product.secret_key" class="text-sm text-orange-600">
              <span class="font-medium">Secret Key:</span> {{ product.secret_key }}
            </p>
          </div>

          <!-- Expand/Collapse Button -->
          <button
            @click="toggleProductDetails(product.id)"
            class="w-full text-orange-600 hover:text-orange-700 text-sm font-medium mb-3 transition-colors flex items-center justify-center"
          >
            <span>{{ expandedProducts.includes(product.id) ? 'Show Less' : 'Show More Details' }}</span>
            <svg 
              :class="['w-4 h-4 ml-1 transition-transform', expandedProducts.includes(product.id) ? 'rotate-180' : '']" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          <!-- Display assigned labels -->
          <div class="flex flex-wrap gap-1 mb-3">
            <span v-for="label in product.labels" :key="label.id" :class="[
              'px-2 py-1 rounded text-xs font-semibold',
              label.type === 'category' ? 'bg-orange-100 text-orange-700 border border-orange-300' :
              label.type === 'subcategory' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
              'bg-amber-100 text-amber-700 border border-amber-300'
            ]">
              {{ label.name }}
            </span>
            <!-- Debug: Show if no labels -->
            <span v-if="!product.labels || product.labels.length === 0" class="text-xs text-gray-400">
              No labels
            </span>
          </div>
          
          <div class="flex items-center justify-between mt-auto">
            <span :class="getStatusClass(product)" class="px-3 py-1 text-xs font-semibold rounded-full">
              {{ getStatusText(product) }}
            </span>
            <div class="flex space-x-3">
              <button @click="editProduct(product)" class="text-orange-600 hover:text-orange-800 text-sm font-medium transition-colors duration-200">
                Edit
              </button>
              <button @click="toggleProductStatus(product)" :class="getToggleClass(product)" class="text-sm font-medium transition-colors duration-200">
                {{ getToggleText(product) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="showPagination" class="mt-8 flex justify-center">
      <div class="bg-white shadow-lg rounded-xl border border-gray-200 p-4">
        <div class="flex items-center space-x-2">
          <button
            @click="changePage(paginationPage - 1)"
            :disabled="paginationPage <= 1"
            class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          
          <div class="flex items-center space-x-1">
            <span class="px-3 py-2 text-sm font-medium text-gray-700">
              Page {{ paginationPage }} of {{ paginationPages }}
            </span>
            <span class="px-3 py-2 text-sm text-gray-500">
              ({{ pagination?.total || 0 }} total products)
            </span>
          </div>
          
          <button
            @click="changePage(paginationPage + 1)"
            :disabled="paginationPage >= paginationPages"
            class="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="filteredProducts.length === 0" class="text-center py-12">
      <div class="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
        <svg class="w-8 h-8 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
        </svg>
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No products found</h3>
      <p class="text-gray-500">Try adjusting your filters or add a new product.</p>
    </div>
  </div>

  <!-- Add/Edit Product Modal -->
  <div v-if="showModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
    <div class="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-2xl rounded-xl bg-white border-gray-200">
      <div class="mt-3">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-xl font-medium text-gray-900">{{ isEditing ? 'Edit Product' : 'Add New Product' }}</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 transition-colors duration-200">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <form @submit.prevent="saveProduct" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">SKU *</label>
              <input
                v-model="formData.sku"
                type="text"
                required
                @blur="validateField('sku')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.sku }"
              />
              <p v-if="errors.sku" class="mt-1 text-sm text-red-600">{{ errors.sku }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Secret Key (Optional)</label>
              <input
                v-model="formData.secret_key"
                type="text"
                placeholder="Leave empty to disable secret access"
                @blur="validateField('secret_key')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.secret_key }"
              />
              <p v-if="errors.secret_key" class="mt-1 text-sm text-red-600">{{ errors.secret_key }}</p>
              <p class="mt-1 text-xs text-gray-500">Leave empty to disable secret access</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Name *</label>
              <input
                v-model="formData.name"
                type="text"
                required
                @blur="validateField('name')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.name }"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <select
                v-model="formData.category"
                required
                @blur="validateField('category')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.category }"
              >
                <option value="">Select Category</option>
                <option value="Gold">Gold</option>
                <option value="Diamond">Diamond</option>
                <option value="Silver">Silver</option>
                <option value="Platinum">Platinum</option>
              </select>
              <p v-if="errors.category" class="mt-1 text-sm text-red-600">{{ errors.category }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Metal</label>
              <input
                v-model="formData.metal"
                type="text"
                placeholder="e.g., 22K, 18K, 925"
                @blur="validateField('metal')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.metal }"
              />
              <p v-if="errors.metal" class="mt-1 text-sm text-red-600">{{ errors.metal }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Vendor Name</label>
              <input
                v-model="formData.vendor_name"
                type="text"
                placeholder="e.g., Premium Gold Suppliers"
                @blur="validateField('vendor_name')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.vendor_name }"
              />
              <p v-if="errors.vendor_name" class="mt-1 text-sm text-red-600">{{ errors.vendor_name }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Gross Weight (grams)</label>
              <input
                v-model="formData.gross_weight"
                type="number"
                step="0.001"
                @blur="validateField('gross_weight')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.gross_weight }"
              />
              <p v-if="errors.gross_weight" class="mt-1 text-sm text-red-600">{{ errors.gross_weight }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Stone Weight (grams)</label>
              <input
                v-model="formData.stone_weight"
                type="number"
                step="0.001"
                @blur="validateField('stone_weight')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.stone_weight }"
              />
              <p v-if="errors.stone_weight" class="mt-1 text-sm text-red-600">{{ errors.stone_weight }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Net Weight (grams)</label>
              <input
                v-model="formData.net_weight"
                type="number"
                step="0.001"
                @input="handleNetWeightChange"
                @blur="validateField('net_weight')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.net_weight }"
              />
              <p v-if="errors.net_weight" class="mt-1 text-sm text-red-600">{{ errors.net_weight }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Purchase Rate (₹)</label>
              <input
                v-model="formData.purchase_rate"
                type="number"
                step="0.01"
                @blur="validateField('purchase_rate')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.purchase_rate }"
              />
              <p v-if="errors.purchase_rate" class="mt-1 text-sm text-red-600">{{ errors.purchase_rate }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Selling Rate (₹)</label>
              <input
                v-model="formData.selling_rate"
                type="number"
                step="0.01"
                @blur="validateField('selling_rate')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.selling_rate }"
              />
              <p v-if="errors.selling_rate" class="mt-1 text-sm text-red-600">{{ errors.selling_rate }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Making Charge (₹)</label>
              <input
                v-model="formData.making_charge"
                type="number"
                step="0.01"
                @blur="validateField('making_charge')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.making_charge }"
              />
              <p v-if="errors.making_charge" class="mt-1 text-sm text-red-600">{{ errors.making_charge }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Stone Charge (₹)</label>
              <input
                v-model="formData.stone_charge"
                type="number"
                step="0.01"
                @blur="validateField('stone_charge')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.stone_charge }"
              />
              <p v-if="errors.stone_charge" class="mt-1 text-sm text-red-600">{{ errors.stone_charge }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                v-model="formData.status"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.status }"
              >
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
                <option value="archived">Archived</option>
              </select>
              <p v-if="errors.status" class="mt-1 text-sm text-red-600">{{ errors.status }}</p>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                v-model="formData.description"
                rows="3"
                @blur="validateField('description')"
                class="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                :class="{ 'border-red-500': errors.description }"
              ></textarea>
              <p v-if="errors.description" class="mt-1 text-sm text-red-600">{{ errors.description }}</p>
            </div>
          </div>

          <!-- Label Assignment Section -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Labels (Category, Subcategory, Tag)</label>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div v-for="type in ['category', 'subcategory', 'tag']" :key="type">
                <div class="font-medium text-xs text-gray-500 mb-2 capitalize">{{ type }}</div>
                <div class="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <label v-for="label in labelsStore.groupedLabels[type]" :key="label.id" class="flex items-center space-x-2 text-sm cursor-pointer mb-2">
                    <input type="checkbox" :value="label.id" v-model="assignedLabelIds" />
                    <span class="text-gray-700">{{ label.name }}</span>
                  </label>
                  <div v-if="!labelsStore.groupedLabels[type] || labelsStore.groupedLabels[type].length === 0" class="text-xs text-gray-400 text-center py-2">
                    No {{ type }} labels available
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Image Upload Section -->
          <div class="space-y-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
            
            <!-- Image Upload Area -->
            <div
              @drop="handleDrop"
              @dragover="handleDragOver"
              @dragleave="handleDragLeave"
              @click="triggerFileInput"
              :class="[
                'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200',
                isDragging 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-200 hover:border-orange-400 hover:bg-orange-50'
              ]"
            >
              <input
                ref="fileInput"
                type="file"
                multiple
                accept="image/*"
                @change="handleImageUpload"
                class="hidden"
              />
              
              <div class="space-y-3">
                <svg class="w-12 h-12 mx-auto text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <div>
                  <p class="text-sm font-medium text-gray-700">
                    <span class="text-orange-600">Click to upload</span> or drag and drop
                  </p>
                  <p class="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB each</p>
                </div>
              </div>
            </div>

            <!-- Image Previews -->
            <div v-if="uploadedImages.length > 0" class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div
                v-for="image in uploadedImages"
                :key="image.id"
                class="relative group aspect-square rounded-lg overflow-hidden border border-gray-200"
              >
                <img
                  :src="getImageUrl(image.url)"
                  :alt="image.name"
                  class="w-full h-full object-contain"
                />
                <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <button
                    @click="removeImage(image.id)"
                    class="w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors duration-200"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div class="absolute bottom-1 left-1 right-1">
                  <p class="text-xs text-white bg-black/70 rounded px-2 py-1 truncate">
                    {{ image.name }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              @click="closeModal"
              class="px-6 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-all duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading">Saving...</span>
              <span v-else>{{ isEditing ? 'Update Product' : 'Add Product' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Image Viewer Modal -->
  <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" @click="closeImageModal"></div>
    <div class="relative bg-white rounded-2xl shadow-2xl border border-gray-200/20 max-w-4xl w-full max-h-[90vh] overflow-hidden">
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200/50">
        <div class="flex items-center">
          <div class="w-10 h-10 mr-3">
            <img 
              src="/logo6.png" 
              alt="Annzz Logo" 
              class="w-full h-full object-contain"
            />
          </div>
          <div>
            <h3 class="text-xl font-bold text-gray-900">{{ selectedProduct?.name }}</h3>
            <p class="text-sm text-gray-600">Product Images</p>
          </div>
        </div>
        <button 
          @click="closeImageModal"
          class="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
        >
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Image Content -->
      <div class="p-6">
        <div v-if="productImages.length > 0" class="space-y-6">
          <!-- Main Image Display -->
          <div class="relative">
            <div class="w-full max-w-3xl mx-auto aspect-w-16 aspect-h-9 bg-black rounded-xl overflow-hidden flex items-center justify-center" style="min-height:360px; max-height:480px;">
              <img 
                :src="getImageUrl(currentImage)" 
                :alt="`${selectedProduct?.name} - Image ${currentImageIndex + 1}`"
                class="w-full h-full object-contain object-center"
                style="min-height:360px; max-height:480px;"
                @error="handleImageError"
              />
            </div>
            
            <!-- Navigation Arrows -->
            <button 
              v-if="productImages.length > 1"
              @click="previousImage"
              class="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
            >
              <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              v-if="productImages.length > 1"
              @click="nextImage"
              class="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
            >
              <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <!-- Image Counter -->
            <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1 text-white text-sm font-medium">
              {{ currentImageIndex + 1 }} / {{ productImages.length }}
            </div>
          </div>

          <!-- Thumbnail Navigation -->
          <div v-if="productImages.length > 1" class="flex space-x-2 overflow-x-auto pb-2">
            <button
              v-for="(image, index) in productImages"
              :key="index"
              @click="setCurrentImage(index)"
              :class="[
                'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200',
                currentImageIndex === index 
                  ? 'border-orange-500 shadow-lg' 
                  : 'border-gray-200 hover:border-orange-300'
              ]"
            >
              <img 
                :src="getImageUrl(image)" 
                :alt="`Thumbnail ${index + 1}`"
                class="w-full h-full object-contain"
                @error="handleImageError"
              />
            </button>
          </div>
        </div>

        <!-- No Images State -->
        <div v-else class="text-center py-12">
          <div class="w-16 h-16 bg-gradient-to-br from-orange-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Images Available</h3>
          <p class="text-gray-500">This product doesn't have any images yet.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { adminAPI } from '../../services/api'
import { useNotificationStore } from '../../stores/notification'
import { useLabelsStore } from '../../stores/labels'
import { useFiltersStore } from '../../stores/filters'
import GlobalFilters from '../../components/GlobalFilters.vue'
import { PAGINATION_LIMITS, createPagination } from '../../constants/pagination'

export default {
  name: 'AdminProducts',
  components: {
    GlobalFilters
  },
  setup() {
    const loading = ref(false)
    const showModal = ref(false)
    const isEditing = ref(false)
    const editingProductId = ref(null)
    
    // Image viewer state
    const showImageModal = ref(false)
    const selectedProduct = ref(null)
    const currentImageIndex = ref(0)

    // Stores
    const notificationStore = useNotificationStore()
    const labelsStore = useLabelsStore()
    const filtersStore = useFiltersStore()

    // Form data
    const formData = ref({
      sku: '',
      name: '',
      category: '',
      metal: '',
      vendor_name: '',
      gross_weight: '',
      stone_weight: '',
      net_weight: '',
      purchase_rate: '',
      selling_rate: '',
      making_charge: '',
      stone_charge: '',
      description: '',
      visible: true,
      status: 'visible', // Added for new form
      secret_key: '', // Added for new form
      images: [] // Add images array
    })

    // Track if net weight was manually edited
    const netWeightManuallyEdited = ref(false)

    // Form errors
    const errors = ref({})

    // Products data
    const products = ref([])
    const pagination = ref(createPagination(PAGINATION_LIMITS.PRODUCTS))

    // Image upload state
    const uploadedImages = ref([])
    const isDragging = ref(false)
    const fileInput = ref(null)

    // Expandable product details state
    const expandedProducts = ref([])

    // Use products directly from backend (no client-side filtering)
    const filteredProducts = computed(() => {
      return products.value || []
    })

    const hasActiveFilters = computed(() => {
      return filtersStore.hasActiveFilters
    })

    const paginationPages = computed(() => {
      return pagination.value?.pages || 0
    })

    const paginationPage = computed(() => {
      return pagination.value?.page || 1
    })

    const showPagination = computed(() => {
      return paginationPages.value > 1
    })

    const getStatusClass = (product) => {
      if (product.archived) return 'bg-gray-100 text-gray-800'
      if (!product.visible) return 'bg-orange-100 text-orange-800'
      return 'bg-green-100 text-green-800'
    }

    const getStatusText = (product) => {
      if (product.archived) return 'Archived'
      if (!product.visible) return 'Hidden'
      return 'Visible'
    }

    const getToggleClass = (product) => {
      if (product.archived) return 'text-orange-600 hover:text-orange-800'
      if (!product.visible) return 'text-orange-600 hover:text-orange-800'
      return 'text-orange-600 hover:text-orange-800'
    }

    const getToggleText = (product) => {
      if (product.archived) return 'Restore'
      if (!product.visible) return 'Show'
      return 'Hide'
    }

    const getProductImages = (product) => {
      if (!product.images) return []
      try {
        return Array.isArray(product.images) 
          ? product.images 
          : JSON.parse(product.images)
      } catch {
        return []
      }
    }

    const openAddProductModal = () => {
      isEditing.value = false
      editingProductId.value = null
      resetForm()
      resetManualEditFlag()
      showModal.value = true
    }

    const editProduct = (product) => {
      isEditing.value = true
      editingProductId.value = product.id
      Object.assign(formData.value, {
        sku: product.sku,
        name: product.name,
        category: product.category,
        metal: product.metal || '',
        vendor_name: product.vendor_name || '',
        gross_weight: product.gross_weight || '',
        stone_weight: product.stone_weight || '0', // Handle NULL as 0
        net_weight: product.net_weight || '',
        purchase_rate: product.purchase_rate || '',
        selling_rate: product.selling_rate || '',
        making_charge: product.making_charge || '',
        stone_charge: product.stone_charge || '',
        description: product.description || '',
        visible: product.visible,
        status: product.visible ? 'visible' : 'hidden', // Map visible to status
        secret_key: product.secret_key || '', // Map secret_key
        images: getProductImages(product) // Load existing images
      })
      
      // Reset manual edit flag and auto-calculate net weight for existing products
      resetManualEditFlag()
      calculateNetWeight()
      
      // Load existing images for editing
      uploadedImages.value = getProductImages(product).map((imageUrl, index) => ({
        id: `existing-${index}`,
        url: imageUrl,
        name: `Image ${index + 1}`,
        file: null // No file object for existing images
      }))
      
      // Load assigned labels for editing
      assignedLabelIds.value = product.labels ? product.labels.map(label => label.id) : []
      
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      resetForm()
    }

    const resetForm = () => {
      Object.assign(formData.value, {
        sku: '',
        name: '',
        category: '',
        metal: '',
        vendor_name: '',
        gross_weight: '',
        stone_weight: '',
        net_weight: '',
        purchase_rate: '',
        selling_rate: '',
        making_charge: '',
        stone_charge: '',
        description: '',
        visible: true,
        status: 'visible', // Reset status
        secret_key: '', // Reset secret_key
        images: [] // Reset images
      })
      errors.value = {}
      uploadedImages.value = [] // Clear uploaded images
      assignedLabelIds.value = [] // Clear assigned labels
    }

    const validateField = (field) => {
      if (field === 'sku') {
        // Don't clear backend errors (like duplicate SKU)
        if (errors.value.sku && errors.value.sku.includes('already exists')) {
          return
        }
        
        if (!formData.value.sku.trim()) {
          errors.value.sku = 'SKU is required'
        } else if (formData.value.sku.trim().length < 2) {
          errors.value.sku = 'SKU must be at least 2 characters'
        } else if (formData.value.sku.trim().length > 50) {
          errors.value.sku = 'SKU must be less than 50 characters'
        } else {
          errors.value.sku = ''
        }
      } else if (field === 'name') {
        if (!formData.value.name.trim()) {
          errors.value.name = 'Name is required'
        } else if (formData.value.name.trim().length < 2) {
          errors.value.name = 'Name must be at least 2 characters'
        } else if (formData.value.name.trim().length > 255) {
          errors.value.name = 'Name must be less than 255 characters'
        } else {
          errors.value.name = ''
        }
      } else if (field === 'category') {
        if (!formData.value.category) {
          errors.value.category = 'Category is required'
        } else {
          errors.value.category = ''
        }
      } else if (field === 'metal') {
        if (formData.value.metal && formData.value.metal.trim().length > 50) {
          errors.value.metal = 'Metal must be less than 50 characters'
        } else {
          errors.value.metal = ''
        }
      } else if (field === 'gross_weight') {
        if (formData.value.gross_weight) {
          const weight = parseFloat(formData.value.gross_weight)
          if (isNaN(weight) || weight < 0) {
            errors.value.gross_weight = 'Gross weight must be a positive number'
          } else {
            errors.value.gross_weight = ''
          }
        } else {
          errors.value.gross_weight = ''
        }
      } else if (field === 'stone_weight') {
        if (formData.value.stone_weight) {
          const weight = parseFloat(formData.value.stone_weight)
          if (isNaN(weight) || weight < 0) {
            errors.value.stone_weight = 'Stone weight must be a positive number'
          } else {
            errors.value.stone_weight = ''
          }
        } else {
          errors.value.stone_weight = ''
        }
      } else if (field === 'net_weight') {
        if (formData.value.net_weight) {
          const weight = parseFloat(formData.value.net_weight)
          if (isNaN(weight) || weight < 0) {
            errors.value.net_weight = 'Net weight must be a positive number'
          } else {
            errors.value.net_weight = ''
          }
        } else {
          errors.value.net_weight = ''
        }
      } else if (field === 'vendor_name') {
        if (formData.value.vendor_name && formData.value.vendor_name.trim().length > 255) {
          errors.value.vendor_name = 'Vendor name must be less than 255 characters'
        } else {
          errors.value.vendor_name = ''
        }
      } else if (field === 'purchase_rate') {
        if (formData.value.purchase_rate) {
          const rate = parseFloat(formData.value.purchase_rate)
          if (isNaN(rate) || rate < 0) {
            errors.value.purchase_rate = 'Purchase rate must be a positive number'
          } else {
            errors.value.purchase_rate = ''
          }
        } else {
          errors.value.purchase_rate = ''
        }
      } else if (field === 'selling_rate') {
        if (formData.value.selling_rate) {
          const rate = parseFloat(formData.value.selling_rate)
          if (isNaN(rate) || rate < 0) {
            errors.value.selling_rate = 'Selling rate must be a positive number'
          } else {
            errors.value.selling_rate = ''
          }
        } else {
          errors.value.selling_rate = ''
        }
      } else if (field === 'making_charge') {
        if (formData.value.making_charge) {
          const charge = parseFloat(formData.value.making_charge)
          if (isNaN(charge) || charge < 0) {
            errors.value.making_charge = 'Making charge must be a positive number'
          } else {
            errors.value.making_charge = ''
          }
        } else {
          errors.value.making_charge = ''
        }
      } else if (field === 'stone_charge') {
        if (formData.value.stone_charge) {
          const charge = parseFloat(formData.value.stone_charge)
          if (isNaN(charge) || charge < 0) {
            errors.value.stone_charge = 'Stone charge must be a positive number'
          } else {
            errors.value.stone_charge = ''
          }
        } else {
          errors.value.stone_charge = ''
        }
      } else if (field === 'secret_key') {
        // Don't clear backend errors (like duplicate secret key)
        if (errors.value.secret_key && errors.value.secret_key.includes('already exists')) {
          return
        }
        
        if (formData.value.secret_key && formData.value.secret_key.trim()) {
          if (formData.value.secret_key.trim().length < 3) {
            errors.value.secret_key = 'Secret key must be at least 3 characters'
          } else if (formData.value.secret_key.trim().length > 100) {
            errors.value.secret_key = 'Secret key must be less than 100 characters'
          } else if (!/^[A-Za-z0-9-_]+$/.test(formData.value.secret_key.trim())) {
            errors.value.secret_key = 'Secret key can only contain letters, numbers, hyphens, and underscores'
          } else {
            errors.value.secret_key = ''
          }
        } else {
          errors.value.secret_key = ''
        }
      } else if (field === 'description') {
        if (formData.value.description && formData.value.description.trim().length > 1000) {
          errors.value.description = 'Description must be less than 1000 characters'
        } else {
          errors.value.description = ''
        }
      }
    }

    const validateForm = () => {
      validateField('sku')
      validateField('name')
      validateField('category')
      validateField('metal')
      validateField('vendor_name')
      validateField('gross_weight')
      validateField('stone_weight')
      validateField('net_weight')
      validateField('purchase_rate')
      validateField('selling_rate')
      validateField('making_charge')
      validateField('stone_charge')
      validateField('secret_key')
      validateField('description')

      // Check if any errors exist
      return Object.keys(errors.value).filter(key => errors.value[key]).length === 0
    }

    const saveProduct = async () => {
      // Clear any previous backend errors before validation
      const backendErrors = ['sku', 'secret_key']
      backendErrors.forEach(field => {
        if (errors.value[field] && errors.value[field].includes('already exists')) {
          delete errors.value[field]
        }
      })
      
      if (!validateForm()) return

      loading.value = true
      try {
        const productData = { ...formData.value }
        
        // Convert numeric fields; leave blank optional ones out, since the API
        // rejects an empty string as "not a number"
        const numericFields = ['gross_weight', 'stone_weight', 'net_weight', 'purchase_rate', 'selling_rate', 'making_charge', 'stone_charge']
        for (const field of numericFields) {
          if (productData[field] === '' || productData[field] === null || productData[field] === undefined) {
            delete productData[field]
          } else {
            productData[field] = parseFloat(productData[field])
          }
        }

        // Map status to visible
        if (productData.status) {
          productData.visible = productData.status === 'visible'
          delete productData.status // Remove status field as backend doesn't expect it
        }

        // Handle empty secret_key - remove it if empty or whitespace
        if (!productData.secret_key || productData.secret_key.trim() === '') {
          delete productData.secret_key
        } else {
          productData.secret_key = productData.secret_key.trim()
        }

        // Handle image uploads - use actual uploaded image URLs
        if (uploadedImages.value.length > 0) {
          productData.images = uploadedImages.value.map(img => img.url)
        } else {
          productData.images = []
        }

        if (isEditing.value) {
          // Update existing product
          await adminAPI.updateProduct(editingProductId.value, productData)
          notificationStore.showSuccess('Product Updated', 'Product has been updated successfully')
        } else {
          // Create new product
          const newProduct = await adminAPI.createProduct(productData)
          notificationStore.showSuccess('Product Created', 'Product has been created successfully')
          editingProductId.value = newProduct.data.product.id // Store the new product ID for label assignment
        }

        // Handle label assignment for both creating and updating
        if (editingProductId.value && assignedLabelIds.value.length > 0) {
          await adminAPI.assignLabels(editingProductId.value, assignedLabelIds.value)
          notificationStore.showSuccess('Labels Assigned', 'Labels have been assigned to this product')
        }

        // Refresh products list
        await loadProducts()
        closeModal()
      } catch (error) {
        console.error('Error saving product:', error)
        
        // Handle different types of errors
        if (error.response?.data?.error === 'DuplicateSKU') {
          errors.value.sku = 'Product with this SKU already exists'
          notificationStore.showError('Duplicate SKU', 'A product with this SKU already exists')
        } else if (error.response?.data?.error === 'DuplicateSecretKey') {
          errors.value.secret_key = 'Product with this secret key already exists'
          notificationStore.showError('Duplicate Secret Key', 'A product with this secret key already exists')
        } else if (error.response?.data?.error === 'ValidationError') {
          // Handle validation errors from backend
          const message = error.response?.data?.message || 'Validation failed'
          
          // Try to map common validation messages to form fields
          if (message.toLowerCase().includes('sku')) {
            errors.value.sku = message
          } else if (message.toLowerCase().includes('name')) {
            errors.value.name = message
          } else if (message.toLowerCase().includes('category')) {
            errors.value.category = message
          } else if (message.toLowerCase().includes('metal')) {
            errors.value.metal = message
          } else if (message.toLowerCase().includes('gross weight')) {
            errors.value.gross_weight = message
          } else if (message.toLowerCase().includes('stone weight')) {
            errors.value.stone_weight = message
          } else if (message.toLowerCase().includes('net weight')) {
            errors.value.net_weight = message
          } else if (message.toLowerCase().includes('vendor name')) {
            errors.value.vendor_name = message
          } else if (message.toLowerCase().includes('purchase rate')) {
            errors.value.purchase_rate = message
          } else if (message.toLowerCase().includes('selling rate')) {
            errors.value.selling_rate = message
          } else if (message.toLowerCase().includes('making charge')) {
            errors.value.making_charge = message
          } else if (message.toLowerCase().includes('stone charge')) {
            errors.value.stone_charge = message
          } else if (message.toLowerCase().includes('description')) {
            errors.value.description = message
          } else if (message.toLowerCase().includes('secret key')) {
            errors.value.secret_key = message
          } else {
            // If we can't map to a specific field, show as general error
            notificationStore.showError('Validation Error', message)
          }
        } else if (error.response?.data?.message) {
          // Handle other backend errors
          notificationStore.showError('Error', error.response.data.message)
        } else {
          notificationStore.showError('Error', 'Failed to save product. Please try again.')
        }
      } finally {
        loading.value = false
      }
    }

    const toggleProductStatus = async (product) => {
      try {
        if (product.archived) {
          await adminAPI.updateProduct(product.id, { archived: false })
          notificationStore.showSuccess('Product Restored', 'Product has been restored successfully')
        } else if (!product.visible) {
          await adminAPI.updateProduct(product.id, { visible: true })
          notificationStore.showSuccess('Product Shown', 'Product is now visible')
        } else {
          await adminAPI.updateProduct(product.id, { visible: false })
          notificationStore.showSuccess('Product Hidden', 'Product is now hidden')
        }
        await loadProducts()
      } catch (error) {
        console.error('Error toggling product status:', error)
        notificationStore.showError('Error', 'Failed to update product status. Please try again.')
      }
    }

    const loadProducts = async () => {
      try {
        loading.value = true
        const params = {
          page: pagination.value.page,
          limit: pagination.value.limit,
          ...filtersStore.filterParams
        }

        console.log('Loading products with params:', params)
        const response = await adminAPI.getProducts(params)
        products.value = response.data.products || []
        pagination.value = response.data.pagination || pagination.value
      } catch (error) {
        console.error('Error loading products:', error)
        notificationStore.showError('Error', 'Failed to load products. Please try again.')
        products.value = []
      } finally {
        loading.value = false
      }
    }

    const getImageUrl = (imageName) => {
      // Return placeholder if no image name
      if (!imageName) {
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xOTIgMTI4SDE2MFY5NkgxOTJWMTA0SDE2OFYxMjhIMTkyWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjA4IDEyOEgyMjRWMTA0SDIwOFY5NkgyMzJWMTA0SDIyNFYxMjhIMjA4WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjE2IDEyOEgyMzJWMTA0SDIxNlY5NkgyNDBWMTA0SDIzMlYxMjhIMjE2WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjI0IDEyOEgyNDBWMTA0SDIyNFY5NkgyNDhWMTA0SDI0MFYxMjhIMjI0WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjMyIDEyOEgyNDhWMTA0SDIzMlY5NkgyNTZWMTA0SDI0OFYxMjhIMjMyWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjQwIDEyOEgyNTZWMTA0SDI0MFY5NkgyNjRWMTA0SDI1NlYxMjhIMjQwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjQ4IDEyOEgyNjRWMTA0SDI0OFY5NkgyNzJWMTA0SDI2NFYxMjhIMjQ4WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjU2IDEyOEgyNzJWMTA0SDM1NlY5NkgyODBWMTA0SDI3MlYxMjhIMjU2WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjY0IDEyOEgyODBWMTA0SDI2NFY5NkgyODhWMTA0SDI4MFYxMjhIMjY0WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMjcyIDEyOEgyODhWMTA0SDI3MlY5NkgyOTZWMTA0SDI4OFYxMjhIMzcyWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMzgwIDEyOEgyOTJWMTA0SDM4MFY5NkgyMDRWMTA0SDI5NlYxMjhIMzgwWiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMzg4IDEyOEgyMDRWMTA0SDM4OFY5NkgyMTJWMTA0SDIwNFYxMjhIMzg4WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMzk2IDEyOEgyMTJWMTA0SDM5NlY5NkgyMjBWMTA0SDIxMlYxMjhIMzk2WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNNDA0IDEyOEgyMjBWMTA0SDQwNFY5NkgyMjhWMTA0SDIyMFYxMjhINDA0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K'
      }
      
      // If it's a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    const productImages = computed(() => {
      if (!selectedProduct.value) return []
      return getProductImages(selectedProduct.value)
    })

    const currentImage = computed(() => {
      if (productImages.value.length === 0) return ''
      return productImages.value[currentImageIndex.value] || productImages.value[0]
    })

    const handleImageError = (event) => {
      // Replace broken image with placeholder
      event.target.style.display = 'none'
      const placeholder = event.target.parentElement.querySelector('.placeholder')
      if (placeholder) {
        placeholder.style.display = 'flex'
      }
    }

    // Image viewer methods
    const openImageModal = (product) => {
      selectedProduct.value = product
      currentImageIndex.value = 0
      showImageModal.value = true
    }

    const closeImageModal = () => {
      showImageModal.value = false
      selectedProduct.value = null
      currentImageIndex.value = 0
    }

    const nextImage = () => {
      if (productImages.value.length > 1) {
        currentImageIndex.value = (currentImageIndex.value + 1) % productImages.value.length
      }
    }

    const previousImage = () => {
      if (productImages.value.length > 1) {
        currentImageIndex.value = currentImageIndex.value === 0 
          ? productImages.value.length - 1 
          : currentImageIndex.value - 1
      }
    }

    const setCurrentImage = (index) => {
      currentImageIndex.value = index
    }

    // Image upload methods
    const handleImageUpload = (event) => {
      const files = Array.from(event.target.files)
      processImageFiles(files)
    }

    const handleDrop = (event) => {
      event.preventDefault()
      isDragging.value = false
      const files = Array.from(event.dataTransfer.files)
      processImageFiles(files)
    }

    const handleDragOver = (event) => {
      event.preventDefault()
      isDragging.value = true
    }

    const handleDragLeave = (event) => {
      event.preventDefault()
      isDragging.value = false
    }

    const processImageFiles = async (files) => {
      const imageFiles = files.filter(file => file.type.startsWith('image/'))
      
      if (imageFiles.length === 0) {
        notificationStore.showError('Invalid Files', 'Please select valid image files (PNG, JPG, GIF)')
        return
      }

      try {
        // Upload files to server
        const response = await adminAPI.uploadImages(imageFiles)
        
        // Add uploaded files to the list
        response.data.files.forEach(file => {
          uploadedImages.value.push({
            id: Date.now() + Math.random(),
            file: null, // No file object for server-uploaded images
            url: file.url,
            name: file.originalName,
            filename: file.filename // Store server filename for deletion
          })
        })

        notificationStore.showSuccess('Images Uploaded', `${imageFiles.length} image(s) uploaded successfully`)
        
      } catch (error) {
        console.error('Error uploading images:', error)
        if (error.response?.data?.error === 'FileTooLarge') {
          notificationStore.showError('File Too Large', 'Image size must be less than 10MB')
        } else if (error.response?.data?.error === 'TooManyFiles') {
          notificationStore.showError('Too Many Files', 'Maximum 10 images allowed')
        } else if (error.response?.data?.error === 'InvalidFile') {
          notificationStore.showError('Invalid File', 'Only image files are allowed')
        } else {
          notificationStore.showError('Upload Failed', 'Failed to upload images. Please try again.')
        }
      }
    }

    const removeImage = (imageId) => {
      const index = uploadedImages.value.findIndex(img => img.id === imageId)
      if (index > -1) {
        uploadedImages.value.splice(index, 1)
      }
    }

    const triggerFileInput = () => {
      fileInput.value?.click()
    }

    const handleFilterChange = (filters) => {
      // Filter change is handled by the computed filteredProducts
      console.log('Filter change:', filters)
    }


    const changePage = (page) => {
      if (page >= 1 && page <= paginationPages.value) {
        pagination.value.page = page
        loadProducts()
      }
    }

    const toggleProductDetails = (productId) => {
      const index = expandedProducts.value.indexOf(productId)
      if (index > -1) {
        expandedProducts.value.splice(index, 1)
      } else {
        expandedProducts.value.push(productId)
      }
    }

    // Label management methods
    const assignedLabelIds = ref([])

    // Auto-calculate net weight when gross weight or stone weight changes
    const calculateNetWeight = () => {
      // Only auto-calculate if net weight wasn't manually edited
      if (netWeightManuallyEdited.value) {
        return
      }
      
      const grossWeight = parseFloat(formData.value.gross_weight) || 0
      const stoneWeight = parseFloat(formData.value.stone_weight) || 0
      
      if (grossWeight > 0) {
        const calculatedNetWeight = grossWeight - stoneWeight
        formData.value.net_weight = calculatedNetWeight.toFixed(3)
      } else {
        formData.value.net_weight = ''
      }
    }

    // Handle manual net weight editing
    const handleNetWeightChange = () => {
      netWeightManuallyEdited.value = true
    }

    // Reset manual edit flag when starting new product
    const resetManualEditFlag = () => {
      netWeightManuallyEdited.value = false
    }

    // Watch for gross weight changes
    watch(() => formData.value.gross_weight, () => {
      calculateNetWeight()
    })

    // Watch for stone weight changes
    watch(() => formData.value.stone_weight, () => {
      calculateNetWeight()
    })

    // Watch for filter changes
    watch(() => filtersStore.filterParams, () => {
      pagination.value.page = 1
      loadProducts()
    }, { deep: true })

    onMounted(() => {
      loadProducts()
      labelsStore.fetchLabelsGrouped()
    })

    return {
      // State
      products,
      pagination,
      loading,
      showModal,
      isEditing,
      editingProductId,
      formData,
      errors,
      uploadedImages,
      isDragging,
      expandedProducts,
      showImageModal,
      selectedProduct,
      currentImageIndex,
      productImages,
      currentImage,
      filtersStore,
      
      // Computed
      filteredProducts,
      hasActiveFilters,
      paginationPages,
      paginationPage,
      showPagination,
      getStatusClass,
      getStatusText,
      getToggleClass,
      getToggleText,
      getProductImages,
      
      // Methods
      openAddProductModal,
      editProduct,
      closeModal,
      saveProduct,
      toggleProductStatus,
      validateField,
      loadProducts,
      resetForm,
      validateForm,
      processImageFiles,
      handleFilterChange,
      // Image upload methods
      fileInput,
      handleImageUpload,
      handleDrop,
      handleDragOver,
      handleDragLeave,
      triggerFileInput,
      removeImage,
      openImageModal,
      closeImageModal,
      nextImage,
      previousImage,
      setCurrentImage,
      getImageUrl,
      handleImageError,
      changePage,
      toggleProductDetails,
      // Label management methods
      assignedLabelIds,
      labelsStore,
      
      // Net weight calculation methods
      handleNetWeightChange,
      resetManualEditFlag,
    }
  }
}
</script> 